import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Brain, Heart, Calendar, Trash2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth } from '../../App';
import CoachNoteCard from '../../components/CoachNoteCard';

const KpiCard = ({ kpi, icon: Icon, color, session }) => {
  if (!session) {
    return (
      <div className="bg-white border border-dashed border-[#E7E5E4] p-5 opacity-50 text-center">
        <span className="w-10 h-10 rounded-full bg-[#F2F0E9] flex items-center justify-center mx-auto mb-2">
          <Icon className="w-5 h-5 text-[#A8A29E]" />
        </span>
        <p className="text-xs uppercase tracking-wider text-[#737373]">{kpi}</p>
        <p className="text-[#A8A29E] text-sm mt-1">Not logged</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#E7E5E4] p-5" data-testid={`round-${kpi}-card`}>
      <div className="flex items-start justify-between mb-3">
        <span className="w-10 h-10 rounded-full text-white flex items-center justify-center" style={{ backgroundColor: color }}>
          <Icon className="w-5 h-5" />
        </span>
        <span className="text-[10px] uppercase tracking-wider font-semibold bg-[#F2F0E9] text-[#57534E] px-2 py-0.5">{session.mode}</span>
      </div>
      <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-1">{kpi}</p>
      <p className="font-['Playfair_Display'] text-3xl tabular-nums" style={{ color }}>
        {session.overall}<span className="text-sm text-[#737373]"> / 10</span>
      </p>
      {session.sub_scores && Object.values(session.sub_scores).some((v) => v != null) && (
        <div className="mt-3 space-y-1">
          {Object.entries(session.sub_scores).map(([k, v]) => v != null && (
            <div key={k} className="flex items-center justify-between text-xs">
              <span className="text-[#57534E] capitalize">{k.replace(/_/g, ' ')}</span>
              <span className="font-semibold text-[#1A1A1A]">{v}/10</span>
            </div>
          ))}
        </div>
      )}
      {session.reflection && (
        <div className="mt-3 pt-3 border-t border-[#F2F0E9]">
          <p className="text-[10px] uppercase tracking-wider text-[#737373] mb-1">Note</p>
          <p className="text-xs text-[#1A1A1A] whitespace-pre-wrap">{session.reflection}</p>
        </div>
      )}
    </div>
  );
};

const RoundReviewDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get(`/locker/rounds/${id}`);
        setData(data);
      } catch {
        toast.error('Failed to load round review.');
        navigate('/locker/rounds');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this round review AND its 3 linked sessions? Cannot be undone.')) return;
    try {
      await API.delete(`/locker/rounds/${id}`);
      toast.success('Round review deleted.');
      navigate('/locker/rounds');
    } catch {
      toast.error('Failed to delete.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const { review, sessions, coach_notes } = data;

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="round-detail-page">
      <div className="bg-[#1A2E1D] text-white">
        <div className="container mx-auto px-6 md:px-10 py-4 max-w-4xl flex items-center justify-between">
          <Link to="/locker/rounds" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="back-to-rounds">
            <ArrowLeft className="w-4 h-4" /> All round reviews
          </Link>
          <button onClick={handleDelete} className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm" data-testid="delete-round-btn">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>
      </div>

      <main className="container mx-auto px-6 md:px-10 py-10 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
            <Calendar className="w-4 h-4" /> {new Date(review.round_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-2">{review.course_name || 'Round review'}</h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-[#57534E]">
            {review.round_type && <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#F2F0E9] text-[#57534E] px-2 py-0.5">{review.round_type}</span>}
            {review.score != null && <span>Score {review.score}</span>}
          </div>
        </div>

        {/* The Gap panel */}
        {review.gap_aa != null && (
          <div className="bg-[#1A2E1D] text-white p-6 mb-8 grid sm:grid-cols-2 gap-6" data-testid="round-gap-panel">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">The Gap (Awareness − Action)</p>
              <p className="font-['Playfair_Display'] text-4xl tabular-nums">{review.gap_aa > 0 ? `+${review.gap_aa}` : review.gap_aa}</p>
              <p className="text-sm text-white/70 mt-1">
                {review.gap_aa > 1 ? 'You saw it. The next step is acting on it.'
                  : review.gap_aa < -1 ? 'You acted — slow down and notice why.'
                  : 'Aware AND acting. Stay here.'}
              </p>
            </div>
            {review.gap_blend != null && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Blend gap (Awareness − avg)</p>
                <p className="font-['Playfair_Display'] text-4xl tabular-nums">{review.gap_blend > 0 ? `+${review.gap_blend}` : review.gap_blend}</p>
                <p className="text-sm text-white/70 mt-1">Awareness vs. average of Acceptance + Action.</p>
              </div>
            )}
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <KpiCard kpi="awareness" icon={Brain} color="#2C5530" session={sessions?.awareness} />
          <KpiCard kpi="acceptance" icon={Heart} color="#BC562B" session={sessions?.acceptance} />
          <KpiCard kpi="action" icon={ArrowRight} color="#1A2E1D" session={sessions?.action} />
        </div>

        {/* Overall reflection */}
        {review.overall_reflection && (
          <div className="bg-white border border-[#E7E5E4] p-6 mb-8">
            <p className="text-[10px] uppercase tracking-wider text-[#737373] mb-2">Overall reflection</p>
            <p className="text-[#1A1A1A] whitespace-pre-wrap leading-relaxed">{review.overall_reflection}</p>
          </div>
        )}

        {/* Coach notes */}
        <div className="bg-white border border-[#E7E5E4] p-6" data-testid="coach-notes-section">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-[#1A2E1D]" />
            <h3 className="font-semibold text-[#1A1A1A]">Coach notes</h3>
          </div>
          {coach_notes?.length ? (
            <ul className="space-y-4">
              {coach_notes.map((n) => (
                <li key={n.id}>
                  <CoachNoteCard
                    note={n}
                    currentUserId={user?.id}
                    onChange={(updated) => {
                      setData((d) => ({
                        ...d,
                        coach_notes: d.coach_notes.map((x) => x.id === updated.id ? updated : x),
                      }));
                    }}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[#A8A29E]">No coach notes on this round yet.</p>
          )}
        </div>
      </main>
    </div>
  );
};

export default RoundReviewDetailPage;
